import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Divider,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface User {
  userId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
  isActive: boolean;
}

interface InviteFormData {
  email: string;
  firstName: string;
  lastName: string;
}

interface SyncLogEntry {
  at: string;
  level: string;
  message: string;
}

interface SyncResult {
  tablesSynced: number;
  tablesFailed: number;
  totalRows: number;
  duration: string;
}

interface SyncStatus {
  jobId: string;
  source: string;
  status: 'running' | 'completed' | 'failed';
  progressPercentage: number;
  currentStep: string;
  startedAt: string;
  finishedAt: string | null;
  processedTables: number;
  totalTables: number;
  successfulTables: number;
  failedTables: number;
  totalRows: number;
  error: string | null;
  logs: SyncLogEntry[];
  result?: SyncResult;
}

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab =
    tabFromUrl === 'database' ? 3 : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteFormData, setInviteFormData] = useState<InviteFormData>({
    email: '',
    firstName: '',
    lastName: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncMonitorError, setSyncMonitorError] = useState<string | null>(null);
  const [hourlyBackupLoading, setHourlyBackupLoading] = useState(false);
  const [hourlyBackupStatusLoading, setHourlyBackupStatusLoading] = useState(false);
  const [hourlyBackupStatus, setHourlyBackupStatus] = useState<SyncStatus | null>(null);
  const [hourlyBackupMonitorError, setHourlyBackupMonitorError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 3) {
      setSearchParams({ tab: 'database' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    if (tabFromUrl === 'database') {
      setTabValue(3);
    }
  }, [tabFromUrl]);

  const fetchSuperAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Get all users and filter locally to ensure we only get Super Admins
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter users with role "Super Admin" locally
      if (Array.isArray(response.data)) {
        const superAdmins = response.data.filter(user => user.role === 'Super Admin');
        setUsers(superAdmins);
      } else {
        setUsers([]);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Error fetching super admins:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSuperAdmins();
  }, [fetchSuperAdmins]);

  const handleInviteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInviteFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInviteSubmit = async () => {
    try {
      setInviteLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Get current user's business ID
      const businessId = localStorage.getItem('businessId') || '550e8400-e29b-41d4-a716-446655440000'; // Default to platform business ID if not available

      // First create the super admin user
      const createResponse = await axios.post(
        `${API_BASE_URL}/users`,
        {
          email: inviteFormData.email,
          username: inviteFormData.email.split('@')[0],
          firstName: inviteFormData.firstName,
          lastName: inviteFormData.lastName,
          role: 'Super Admin',
          password: 'TemporaryPassword123!',
          businessId: businessId, // Explicitly set the business ID
          address: 'Platform Admin - No Address Required' // Add required address field
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (createResponse.data.userId) {
        // Then send invitation
        const inviteResponse = await axios.post(
          `${API_BASE_URL}/invitations/send`,
          {
            email: inviteFormData.email,
            firstName: inviteFormData.firstName,
            lastName: inviteFormData.lastName
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (inviteResponse.data.success) {
          setSuccess(`Super Admin ${inviteFormData.firstName} ${inviteFormData.lastName} created and invitation sent`);
          setOpenInviteDialog(false);
          setInviteFormData({
            email: '',
            firstName: '',
            lastName: ''
          });
          fetchSuperAdmins(); // Refresh the list
        }
      }
    } catch (err: any) {
      console.error('Error creating super admin:', err);
      setError(err.response?.data?.message || 'Failed to create super admin');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will be marked as inactive.')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Use PATCH request to update user status to inactive
      await axios.patch(`${API_BASE_URL}/users/${userId}/status`, 
        { isActive: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('User deactivated successfully');
      fetchSuperAdmins(); // Refresh the list
    } catch (err: any) {
      console.error('Error deactivating user:', err);
      setError(err.response?.data?.message || 'Failed to deactivate user');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStaging = async () => {
    if (!window.confirm('This will overwrite the entire staging database with production data. Continue?')) return;
    try {
      setSyncLoading(true);
      setError(null);
      setSuccess(null);
      setSyncMonitorError(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/owner/sync-staging`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const startedJob = response.data?.job || null;
      if (startedJob) {
        setSyncStatus(startedJob);
      } else {
        // Backward-compatible fallback when backend has not been updated with job tracking.
        setSyncStatus({
          jobId: `fallback-${Date.now()}`,
          source: 'manual',
          status: 'running',
          progressPercentage: 0,
          currentStep: 'Sync started. Waiting for live status from backend...',
          startedAt: new Date().toISOString(),
          finishedAt: null,
          processedTables: 0,
          totalTables: 0,
          successfulTables: 0,
          failedTables: 0,
          totalRows: 0,
          error: null,
          logs: []
        });
      }
      setSuccess('Database sync started. Status is shown below.');
      fetchSyncStatus(startedJob?.jobId);
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.job) {
        setSyncStatus(err.response.data.job);
        setError('A sync is already running. Showing live progress below.');
      } else {
        setError(err.response?.data?.message || 'Failed to start database sync');
      }
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchSyncStatus = useCallback(async (jobId?: string) => {
    try {
      setSyncStatusLoading(true);
      setSyncMonitorError(null);
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/owner/sync-staging/status`, {
        headers: { Authorization: `Bearer ${token}` },
        params: jobId ? { jobId } : undefined
      });
      setSyncStatus(response.data?.status || null);
    } catch (err: any) {
      console.error('Error fetching sync status:', err);
      if (err.response?.status === 404) {
        setSyncMonitorError('Live progress endpoint is not available on the current backend deployment yet.');
      } else {
        setSyncMonitorError('Could not fetch live sync progress. Retrying while sync is running.');
      }
    } finally {
      setSyncStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 3) {
      fetchSyncStatus();
    }
  }, [tabValue, fetchSyncStatus]);

  useEffect(() => {
    if (!syncStatus || syncStatus.status !== 'running') {
      return;
    }

    const intervalId = window.setInterval(() => {
      fetchSyncStatus(syncStatus.jobId);
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [syncStatus, fetchSyncStatus]);

  // Hourly backup handlers
  const handleSyncHourlyBackup = async () => {
    if (!window.confirm('This will trigger an immediate hourly backup sync (export current backup DB to Google Drive, then overwrite with production). Continue?')) return;
    try {
      setHourlyBackupLoading(true);
      setError(null);
      setSuccess(null);
      setHourlyBackupMonitorError(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/owner/sync-hourly-backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const startedJob = response.data?.job || null;
      if (startedJob) {
        setHourlyBackupStatus(startedJob);
      } else {
        setHourlyBackupStatus({
          jobId: `fallback-${Date.now()}`,
          source: 'manual',
          status: 'running',
          progressPercentage: 0,
          currentStep: 'Sync triggered. Waiting for status...',
          startedAt: new Date().toISOString(),
          finishedAt: null,
          processedTables: 0,
          totalTables: 0,
          successfulTables: 0,
          failedTables: 0,
          totalRows: 0,
          error: null,
          logs: []
        });
      }
      setSuccess('Hourly backup sync triggered. Status shown below.');
      fetchHourlyBackupStatus();
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.job) {
        setHourlyBackupStatus(err.response.data.job);
        setError('Hourly backup sync is already running. Showing live progress below.');
      } else {
        setError(err.response?.data?.message || 'Failed to trigger hourly backup sync');
      }
    } finally {
      setHourlyBackupLoading(false);
    }
  };

  const fetchHourlyBackupStatus = useCallback(async () => {
    try {
      setHourlyBackupStatusLoading(true);
      setHourlyBackupMonitorError(null);
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/owner/sync-hourly-backup/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHourlyBackupStatus(response.data?.status || null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setHourlyBackupMonitorError('Hourly backup status endpoint not available yet.');
      } else {
        setHourlyBackupMonitorError('Could not fetch hourly backup status.');
      }
    } finally {
      setHourlyBackupStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 3) {
      fetchHourlyBackupStatus();
    }
  }, [tabValue, fetchHourlyBackupStatus]);

  useEffect(() => {
    if (!hourlyBackupStatus || hourlyBackupStatus.status !== 'running') return;
    const intervalId = window.setInterval(() => {
      fetchHourlyBackupStatus();
    }, 2000);
    return () => window.clearInterval(intervalId);
  }, [hourlyBackupStatus, fetchHourlyBackupStatus]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getSyncChipColor = (status?: string) => {
    if (status === 'running') return 'warning';
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'error';
    return 'default';
  };

  const getSyncStatusLabel = (status?: string) => {
    if (status === 'running') return 'Running';
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    return 'No recent sync';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 4 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Users" />
          <Tab label="General" />
          <Tab label="Security" />
          <Tab label="Database Tools" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Super Admin Users</Typography>
            <Button 
              variant="contained" 
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenInviteDialog(true)}
            >
              Create Super Admin
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          {!user.isActive ? (
                            <Chip 
                              label="Inactive"
                              color="error"
                              size="small"
                            />
                          ) : !user.lastLoginAt ? (
                            <Chip 
                              label="Pending"
                              color="warning"
                              size="small"
                            />
                          ) : (
                            <Chip 
                              label="Active"
                              color="success"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(user.lastLoginAt)}</TableCell>
                        <TableCell>
                          <IconButton color="error" onClick={() => handleDeleteUser(user.userId)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No super admin users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            General Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            General platform settings will be available here in a future update.
          </Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Security settings will be available here in a future update.
          </Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Database Tools
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Sync Production → Staging
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Copies the entire production database to staging. All existing staging data will be overwritten.
                  This also runs automatically every 30 minutes.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="warning"
                startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncStaging}
                disabled={syncLoading}
                sx={{ ml: 3, whiteSpace: 'nowrap' }}
              >
                {syncLoading ? 'Starting...' : 'Sync Now'}
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Hourly Backup Sync (Test)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Triggers the sync-service manually: exports the current hourly backup DB to Google Drive, then overwrites it with production data. Runs automatically every hour on the hour.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                startIcon={hourlyBackupLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncHourlyBackup}
                disabled={hourlyBackupLoading || hourlyBackupStatus?.status === 'running'}
                sx={{ ml: 3, whiteSpace: 'nowrap' }}
              >
                {hourlyBackupLoading ? 'Triggering...' : hourlyBackupStatus?.status === 'running' ? 'Running...' : 'Run Now'}
              </Button>
            </Box>
          </Paper>

          {(hourlyBackupStatusLoading || hourlyBackupStatus || hourlyBackupMonitorError) && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Hourly Backup Status</Typography>
                  <Chip
                    label={getSyncStatusLabel(hourlyBackupStatus?.status)}
                    color={getSyncChipColor(hourlyBackupStatus?.status)}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {hourlyBackupStatus?.currentStep || (hourlyBackupStatusLoading ? 'Loading status...' : 'No sync history yet')}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={hourlyBackupStatus?.progressPercentage ?? 0}
                  color={hourlyBackupStatus?.status === 'failed' ? 'error' : 'secondary'}
                />
                <Typography variant="caption" color="text.secondary">
                  {`${hourlyBackupStatus?.progressPercentage ?? 0}%`}
                  {hourlyBackupStatus?.totalTables ? ` • Tables ${hourlyBackupStatus.processedTables}/${hourlyBackupStatus.totalTables}` : ''}
                  {hourlyBackupStatus?.totalRows ? ` • Rows ${hourlyBackupStatus.totalRows.toLocaleString()}` : ''}
                </Typography>
                {hourlyBackupStatus?.error && (
                  <Alert severity="error">Sync failed: {hourlyBackupStatus.error}</Alert>
                )}
                {hourlyBackupMonitorError && (
                  <Alert severity="warning">{hourlyBackupMonitorError}</Alert>
                )}
                {hourlyBackupStatus?.status === 'completed' && (
                  <Alert severity="success">
                    Backup sync completed: {hourlyBackupStatus.successfulTables} tables, {hourlyBackupStatus.totalRows.toLocaleString()} rows.
                  </Alert>
                )}
                {(hourlyBackupStatus?.logs?.length ?? 0) > 0 && (
                  <Box sx={{ maxHeight: 160, overflowY: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 1.25 }}>
                    {(hourlyBackupStatus?.logs ?? []).slice(-8).map((log, index) => (
                      <Typography
                        key={`${log.at}-${index}`}
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        [{new Date(log.at).toLocaleTimeString()}] {log.message}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {(syncStatusLoading || syncStatus || syncMonitorError) && (
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Latest Sync Status</Typography>
                  <Chip
                    label={getSyncStatusLabel(syncStatus?.status)}
                    color={getSyncChipColor(syncStatus?.status)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {syncStatus?.currentStep || (syncStatusLoading ? 'Loading sync status...' : 'No sync history yet')}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={syncStatus?.progressPercentage ?? 0}
                  color={syncStatus?.status === 'failed' ? 'error' : 'primary'}
                />
                <Typography variant="caption" color="text.secondary">
                  {`${syncStatus?.progressPercentage ?? 0}%`}
                  {syncStatus?.totalTables ? ` • Tables ${syncStatus.processedTables}/${syncStatus.totalTables}` : ''}
                  {syncStatus?.totalRows ? ` • Rows ${syncStatus.totalRows.toLocaleString()}` : ''}
                </Typography>

                {syncStatus?.error && (
                  <Alert severity="error">
                    Sync failed: {syncStatus.error}
                  </Alert>
                )}

                {syncMonitorError && (
                  <Alert severity="warning">
                    {syncMonitorError}
                  </Alert>
                )}

                {syncStatus?.status === 'completed' && syncStatus.result && (
                  <Alert severity="success">
                    Sync completed: {syncStatus.result.tablesSynced} tables, {syncStatus.result.totalRows.toLocaleString()} rows in {syncStatus.result.duration}s.
                  </Alert>
                )}

                {(syncStatus?.logs?.length ?? 0) > 0 && (
                  <Box
                    sx={{
                      maxHeight: 160,
                      overflowY: 'auto',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      p: 1.25
                    }}
                  >
                    {(syncStatus?.logs ?? []).slice(-8).map((log, index) => (
                      <Typography
                        key={`${log.at}-${index}`}
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        [{new Date(log.at).toLocaleTimeString()}] {log.message}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </TabPanel>
      </Paper>

      {/* Invite Dialog */}
      <Dialog 
        open={openInviteDialog} 
        onClose={() => setOpenInviteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Super Admin User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={inviteFormData.email}
                onChange={handleInviteChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={inviteFormData.firstName}
                onChange={handleInviteChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={inviteFormData.lastName}
                onChange={handleInviteChange}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInviteDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleInviteSubmit}
            disabled={inviteLoading || !inviteFormData.email || !inviteFormData.firstName || !inviteFormData.lastName}
          >
            {inviteLoading ? <CircularProgress size={24} /> : 'Create & Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings; 